import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import RadioGroupField from '../../../form/RadioGroupField'
import TextAreaField from '../../../form/TextAreaField'
import Button from '../../../ui/button'
import Popup from '../../../ui/popup'
import FAQ from '../../../ui/popup/FAQ'

import Dropzone from 'react-dropzone'
import { Formik, Form, Field, FieldProps } from 'formik'

import { SelectOption } from '../../../../store/commonTypes'
import { UploadPhoto } from '../../../../store/member/memberTypings'
import { RootState } from '../../../../store/rootReducer'

import { photoUpload } from '../../../../store/member/memberSlice'
import { photoUploadError, photoUploadErrorClean } from '../../../../store/member/memberSlice'
import './uploadPhoto.sass'

interface Props {
    onClose: () => void
}

const visibilityOptions: SelectOption[] = [
    {
        value: 'free',
        label: 'Public'
    },
    {
        value: 'private',
        label: 'Private'
    },
    {
        value: 'pro',
        label: 'Premium'
    }
]

const UploadPhotoPopup: React.FunctionComponent<Props> = ({ onClose }: Props) => {
    const dispatch = useDispatch()
    const [t] = useTranslation()

    const { error } = useSelector((state: RootState) => state.member)

    return (
        <Popup title={t('Add a photo')} classname="vertical" onClose={onClose} profile={true}>
            {error && <p className="dropzone__error">{error}</p>}
            <Formik
                initialValues={{
                    image_data: null,
                    album_type: 'free',
                    album_comments: ''
                }}
                onSubmit={(values: UploadPhoto) => {
                    dispatch(photoUpload(values))
                    onClose()
                }}
            >
                {(props) => {
                    const {
                        values: { image_data },
                        setFieldValue,
                        submitForm
                    } = props
                    return (
                        <Form className="form">
                            {!image_data ? (
                                <Field name="image_data">
                                    {({ form }: FieldProps) => (
                                        <Dropzone
                                            accept="image/*"
                                            multiple={false}
                                            onDrop={(acceptedFiles) => {
                                                try {
                                                    const reader = new FileReader()
                                                    reader.onload = (e) => {
                                                        form.setFieldValue('image_data', e.target.result)
                                                    }
                                                    reader.readAsDataURL(acceptedFiles[0])
                                                    dispatch(photoUploadErrorClean())
                                                } catch (err) {
                                                    dispatch(photoUploadError())
                                                }
                                            }}
                                        >
                                            {({ getRootProps, getInputProps }) => (
                                                <div {...getRootProps()} className="dropzone">
                                                    <input {...getInputProps()} />
                                                    <p>{t('Drag your file here')}</p>
                                                    <Button type="button" label={t('Or add from')} />
                                                </div>
                                            )}
                                        </Dropzone>
                                    )}
                                </Field>
                            ) : (
                                <div className="preview">
                                    <img src={image_data} alt="" />
                                    <div className="uploadControls">
                                        <Button label={t('Add')} onClick={submitForm} type="button" />
                                        <Button
                                            tertiary
                                            type="button"
                                            label={t('Reset')}
                                            onClick={() => {
                                                setFieldValue('image_data', null)
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                            <Field name="album_type">
                                {({ field, form }: FieldProps) => (
                                    <RadioGroupField
                                        form={form}
                                        field={field}
                                        label={t('Upload in')}
                                        options={visibilityOptions}
                                    />
                                )}
                            </Field>
                            <Field name="album_comments">
                                {({ field, meta, form }: FieldProps) => (
                                    <TextAreaField
                                        form={form}
                                        field={field}
                                        error={meta.error}
                                        label={t('Description')}
                                        placeholder={t('Photo description')}
                                    />
                                )}
                            </Field>
                        </Form>
                    )
                }}
            </Formik>
            <FAQ>
                <>
                    <p>{t('The uploaded image size should be at least')}</p>
                    <p>{t('Photos of your public album can be viewed by all registered visitors')}</p>
                    <p>{t('Your private album is visible only to the members of your friends list')}</p>
                    <p>{t('Photos of your Premium album can be viewed by Premium members only')}</p>
                    <p>{t('A text description')}</p>
                </>
            </FAQ>
        </Popup>
    )
}

export default UploadPhotoPopup
